/**
 * Rotten Charm — Predictive Search
 * Upgrades the header's search form from a plain "submit to /search" into
 * a live results dropdown, using Shopify's built-in predictive search
 * endpoint (/search/suggest.json) — no external search app required.
 *
 * Follows the ARIA combobox pattern: the input is role="combobox", the
 * results container is role="listbox", each result is role="option".
 * Falls back gracefully to a normal full-page search if JS fails or the
 * fetch errors — the underlying <form> still submits to /search normally.
 */
(function () {
  'use strict';

  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 250;

  function getProductImageUrl(product) {
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    if (product.image) return product.image;
    if (Array.isArray(product.images) && product.images.length) return product.images[0];
    return null;
  }

  class RottenPredictiveSearch {
    constructor(input) {
      this.input = input;
      this.form = input.closest('form');
      this.resultsEl = document.getElementById(input.getAttribute('aria-controls'));
      if (!this.resultsEl) return;

      this.debounceTimer = null;
      this.abortController = null;
      this.activeIndex = -1;
      this.resultLinks = [];

      this.bindInput();
      this.bindKeyboard();
      this.bindOutsideClick();
    }

    bindInput() {
      this.input.addEventListener('input', () => {
        const query = this.input.value.trim();
        clearTimeout(this.debounceTimer);

        if (query.length < MIN_QUERY_LENGTH) {
          this.close();
          return;
        }

        this.debounceTimer = setTimeout(() => this.search(query), DEBOUNCE_MS);
      });
    }

    async search(query) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      this.resultsEl.innerHTML = `<p class="rc-predictive-search__status">${
        window.rcStrings?.searching || 'Searching...'
      }</p>`;
      this.open();

      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
        const res = await fetch(url, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Predictive search request failed');
        const data = await res.json();
        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        this.render(products, query);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Rotten Charm: predictive search failed', err);
        this.renderError();
      }
    }

    render(products, query) {
      if (!products.length) {
        this.resultsEl.innerHTML = `
          <p class="rc-predictive-search__status">
            ${window.rcStrings?.noResults || 'No screenings found for'} \u201c${this.escapeHtml(query)}.\u201d
          </p>
        `;
        this.resultLinks = [];
        this.activeIndex = -1;
        return;
      }

      const itemsHtml = products
        .map((product, i) => {
          const imageUrl = getProductImageUrl(product);
          const priceHtml = product.price
            ? `<span class="rc-predictive-search__price">${this.escapeHtml(product.price)}</span>`
            : '';
          return `
            <a
              href="${product.url}"
              class="rc-predictive-search__result"
              role="option"
              id="rc-predictive-option-${i}"
              data-rc-predictive-result
            >
              ${imageUrl ? `<img src="${imageUrl}" alt="" class="rc-predictive-search__thumb" width="48" height="60" loading="lazy">` : '<span class="rc-predictive-search__thumb rc-predictive-search__thumb--placeholder"></span>'}
              <span class="rc-predictive-search__result-info">
                <span class="rc-predictive-search__title">${this.escapeHtml(product.title)}</span>
                ${priceHtml}
              </span>
            </a>
          `;
        })
        .join('');

      const viewAllUrl = `/search?q=${encodeURIComponent(query)}&type=product`;

      this.resultsEl.innerHTML = `
        <div class="rc-predictive-search__list">${itemsHtml}</div>
        <a href="${viewAllUrl}" class="rc-predictive-search__view-all" data-rc-predictive-view-all>
          ${window.rcStrings?.viewAllResults || 'View all results for'} \u201c${this.escapeHtml(query)}\u201d \u2192
        </a>
      `;

      this.resultLinks = Array.from(this.resultsEl.querySelectorAll('[data-rc-predictive-result]'));
      this.activeIndex = -1;
    }

    renderError() {
      this.resultsEl.innerHTML = `<p class="rc-predictive-search__status">${
        window.rcStrings?.cartError || 'Something went wrong. Try again.'
      }</p>`;
      this.resultLinks = [];
      this.activeIndex = -1;
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    open() {
      this.resultsEl.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.resultsEl.hidden = true;
      this.resultsEl.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.resultLinks = [];
      this.activeIndex = -1;
    }

    bindKeyboard() {
      this.input.addEventListener('keydown', (e) => {
        if (this.resultsEl.hidden) return;

        if (e.key === 'Escape') {
          this.close();
          return;
        }

        if (!this.resultLinks.length) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.activeIndex = Math.min(this.activeIndex + 1, this.resultLinks.length - 1);
          this.updateActiveDescendant();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.activeIndex = Math.max(this.activeIndex - 1, 0);
          this.updateActiveDescendant();
        } else if (e.key === 'Enter' && this.activeIndex >= 0) {
          e.preventDefault();
          this.resultLinks[this.activeIndex].click();
        }
      });
    }

    updateActiveDescendant() {
      this.resultLinks.forEach((link, i) => {
        link.classList.toggle('rc-predictive-search__result--active', i === this.activeIndex);
      });
      const active = this.resultLinks[this.activeIndex];
      if (active) {
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }
    }

    bindOutsideClick() {
      document.addEventListener('click', (e) => {
        if (!this.form || !this.form.contains(e.target)) {
          this.close();
        }
      });
    }
  }

  document.querySelectorAll('[data-rc-search-input]').forEach((input) => new RottenPredictiveSearch(input));
})();
